import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import moment from "moment";
import { useClientView } from "../../../components/TimeTracker/TimeTrackerClient/useClientDetails";
import { useFetchClientInvoices, useFetchClientProjectDetails } from "../../../components/Clients/ClientInvoice/useClientInvoice";
import {
  useTotalBudgetByClient,
  useTotalCostByClient,
  useTotalInvoiceAmountByClient,
} from "../../../features/projects/ProjectList";
import LeftArrow from "../../../images/icons/blue-left-arrow.svg";
import CalendarIcon from "../../../images/icons/calendar-icon.svg";
import ClockIcon from "../../../images/icons/clock.svg";
import DollarIcon from "../../../images/icons/billable.svg";
import profileImage from "../../../images/user/Default_pfp.png";
import MvpClientRevenueChart from "./MvpClientRevenueChart";
import MvpClientChrome from "./MvpClientChrome";
import "./MvpClientDetails.css";

function SortMark() {
  return (
    <span className="mvp-cd-sort" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path d="M0.424316 3.64275L3.21843 0.848633L6.01255 3.64275" strokeWidth="1.2" stroke="#405BFF" />
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path d="M6.0127 0.424632L3.21858 3.21875L0.42446 0.424632" strokeWidth="1.2" stroke="#405BFF" />
      </svg>
    </span>
  );
}

function HoverText({ text }) {
  const clipRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const value = text || "—";

  useEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [value]);

  return (
    <span className="mvp-cd-name">
      <span ref={clipRef} className="mvp-cd-name-clip">{value}</span>
      {truncated && <span className="mvp-cd-name-full">{value}</span>}
    </span>
  );
}

function money(value, currency) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function plainAmount(value) {
  return (Number(value) || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function roiPercent(budget, cost) {
  const budgetValue = Number(budget) || 0;
  const costValue = Number(cost) || 0;
  if (costValue === 0 && budgetValue === 0) return "0%";
  if (costValue === 0) return "N/A";
  const value = ((budgetValue - costValue) / costValue) * 100;
  return Number.isNaN(value) ? "0%" : `${Math.round(value)}%`;
}

function personName(person) {
  if (!person) return "";
  if (person.first_name) {
    const middle = person.middle_name && person.middle_name !== "null" ? person.middle_name : "";
    return `${person.first_name} ${middle} ${person.last_name || ""}`.replace(/\s+/g, " ").trim();
  }
  if (person.company_first_name) {
    return `${person.company_first_name} ${person.company_last_name || ""}`.trim();
  }
  return person.company_name || "";
}

function ManagerAvatar({ manager }) {
  const image = manager?.profile_image && manager.profile_image !== "null" ? manager.profile_image : "";
  const [failed, setFailed] = useState(false);
  const src = image && !failed
    ? `/uploads/lowProfileImage/${image.replace(/[^a-zA-Z0-9._-]/g, "")}`
    : profileImage;
  return <img className="mvp-cd-manager-avatar" src={src} alt="" onError={() => setFailed(true)} />;
}

export default function MvpClientDetails({ clientId, encryptedId }) {
  const user = useSelector((state) => state.auth.user);
  const token = user?.token;
  const tz = useSelector((state) => state.appSettings.appSettings?.timeZone);
  const forex = useSelector((state) => state.forex?.forex);
  const currency = useSelector((state) => state.invoice.dashboardCurrency);
  const encoded = encodeURIComponent(encryptedId || "");

  const { data: client, isLoading: clientLoading } = useClientView(clientId, token);
  const { data: projects = [], isLoading: projectsLoading } = useFetchClientProjectDetails(clientId, token);
  const { data: invoices = [], isLoading: invoicesLoading } = useFetchClientInvoices(clientId, token);
  const { data: totalBudget = 0, isLoading: budgetLoading } = useTotalBudgetByClient(clientId, token);
  const { data: totalCost = 0, isLoading: costLoading } = useTotalCostByClient(clientId, token, null, null, tz, forex, currency);
  const { data: totalRevenue = 0, isLoading: revenueLoading } = useTotalInvoiceAmountByClient(clientId, token);

  const projectList = Array.isArray(projects) ? projects : [];
  const invoiceList = Array.isArray(invoices) ? invoices : [];
  const activeCount = projectList.filter((project) => project.is_active && !project.is_archived).length;
  const preferredCurrency = client?.preferred_currency || "USD";
  const latest = projectList.slice(0, 6);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [managerOpen, setManagerOpen] = useState(true);
  const tableRef = useRef(null);

  useEffect(() => {
    const tableEl = tableRef.current;
    const contentOuter = document.querySelector(".content-outer.clients");
    if (!tableEl || !contentOuter) return undefined;

    let atEnd = false;
    const updatePadding = () => {
      if (window.innerWidth > 700) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
        return;
      }
      const maxScroll = Math.max(0, tableEl.scrollWidth - tableEl.clientWidth);
      if (maxScroll <= 2) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
        return;
      }
      if (!atEnd && tableEl.scrollLeft >= maxScroll - 4) {
        atEnd = true;
        contentOuter.classList.add("mvp-hscroll-end");
      } else if (atEnd && tableEl.scrollLeft < maxScroll - 24) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
      }
    };

    tableEl.addEventListener("scroll", updatePadding, { passive: true });
    window.addEventListener("resize", updatePadding);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePadding) : null;
    resizeObserver?.observe(tableEl);
    updatePadding();

    return () => {
      tableEl.removeEventListener("scroll", updatePadding);
      window.removeEventListener("resize", updatePadding);
      resizeObserver?.disconnect();
      contentOuter.classList.remove("mvp-hscroll-end");
    };
  }, [latest.length]);

  const statusName = (project) => {
    if (project.is_archived) return "Archived";
    if (project.is_active === false) return "Inactive";
    return "Active";
  };

  return (
    <MvpClientChrome client={client} encryptedId={encryptedId}>
      <div className="mvp-cd">
        <div className="mvp-cd-body">
          <div className="mvp-cd-toolbar">
            <NavLink to="/client/overview" className="mvp-cd-back">
              <img src={LeftArrow} alt="" />
              Back to All Clients
            </NavLink>
            <span className="mvp-cd-range">
              <img src={CalendarIcon} alt="" />
              All time
            </span>
          </div>

          <div className="mvp-cd-top">
            <article className="mvp-cd-card mvp-cd-stat">
              <div className="mvp-cd-stat-head">
                <p>Total Revenue</p>
                <img src={ClockIcon} alt="" />
              </div>
              {revenueLoading ? <span className="mvp-cd-skel mvp-cd-skel-value" /> : <strong>{plainAmount(totalRevenue)}</strong>}
            </article>
            <article className="mvp-cd-card mvp-cd-stat">
              <div className="mvp-cd-stat-head">
                <p>Overall ROI</p>
                <img src={ClockIcon} alt="" />
              </div>
              {budgetLoading || costLoading ? <span className="mvp-cd-skel mvp-cd-skel-value is-short" /> : <strong>{roiPercent(totalBudget, totalCost)}</strong>}
            </article>
            <article className="mvp-cd-card mvp-cd-chart">
              <div className="mvp-cd-chart-head">
                <h2>Revenue over time</h2>
                <span className="mvp-cd-chart-search" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="8" cy="8" r="5.1" stroke="#8B95A3" strokeWidth="1.3" />
                    <path d="M11.8 11.8L15.2 15.2" stroke="#8B95A3" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M6.1 9.4V7.1" stroke="#8B95A3" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M8 9.4V5.4" stroke="#8B95A3" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M9.9 9.4V6.3" stroke="#8B95A3" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </span>
              </div>
              {invoicesLoading ? <span className="mvp-cd-skel mvp-cd-skel-chart" /> : <MvpClientRevenueChart invoices={invoiceList} />}
            </article>
            <article className="mvp-cd-card mvp-cd-stat">
              <div className="mvp-cd-stat-head">
                <p>Total Projects</p>
                <img src={DollarIcon} alt="" />
              </div>
              {projectsLoading ? <span className="mvp-cd-skel mvp-cd-skel-value is-short" /> : <strong>{projectList.length}</strong>}
            </article>
            <article className="mvp-cd-card mvp-cd-stat">
              <div className="mvp-cd-stat-head">
                <p>Active Projects</p>
                <img src={DollarIcon} alt="" />
              </div>
              {projectsLoading ? <span className="mvp-cd-skel mvp-cd-skel-value is-short" /> : <strong>{activeCount}</strong>}
            </article>
          </div>

          <div className="mvp-cd-bottom">
            <section className="mvp-cd-card mvp-cd-table-card">
              <div className="mvp-cd-table-head">
                <h2>Latest Projects</h2>
                <NavLink to={`/client/projects/${encoded}`}>View All</NavLink>
              </div>
              <div className="mvp-cd-table-scroll" ref={tableRef}>
                <table>
                  <thead>
                    <tr>
                      <th>Project Name <i /><SortMark /></th>
                      <th>Status <i /><SortMark /></th>
                      <th>Budget <i /><SortMark /></th>
                      <th>ROI <i /><SortMark /></th>
                      <th aria-label="Details" />
                    </tr>
                  </thead>
                  <tbody>
                    {projectsLoading &&
                      Array.from({ length: 6 }).map((_, index) => (
                        <tr key={`overview-skeleton-${index}`} className="mvp-cd-skel-row">
                          <td><span className="mvp-cd-skel mvp-cd-skel-name" /></td>
                          <td><span className="mvp-cd-skel mvp-cd-skel-pill" /></td>
                          <td><span className="mvp-cd-skel mvp-cd-skel-money" /></td>
                          <td><span className="mvp-cd-skel mvp-cd-skel-short" /></td>
                          <td><span className="mvp-cd-skel mvp-cd-skel-btn" /></td>
                        </tr>
                      ))}
                    {!projectsLoading && latest.length === 0 && (
                      <tr>
                        <td colSpan="5" className="mvp-cd-empty">No projects yet</td>
                      </tr>
                    )}
                    {!projectsLoading && latest.map((project) => {
                      const status = statusName(project);
                      return (
                        <tr key={project._id}>
                          <td>
                            <span className="mvp-cd-mobile-row">
                              <HoverText text={project.project_name || "Project"} />
                              <NavLink className="mvp-cd-mobile-go" to={`/client/projects/details/${project._id}/${encoded}`} aria-label="Open project">
                                <svg width="8" height="12" viewBox="0 0 8 12" aria-hidden="true">
                                  <path d="M1.4 1.2 6.2 6 1.4 10.8" fill="none" stroke="#98A2B3" strokeWidth="1.4" strokeLinecap="round" />
                                </svg>
                              </NavLink>
                            </span>
                          </td>
                          <td>
                            <span className={`mvp-cd-pill ${status === "Active" ? "is-active" : "is-done"}`}>{status}</span>
                          </td>
                          <td>{money(project.budget, preferredCurrency)}</td>
                          <td>—</td>
                          <td>
                            <NavLink className="mvp-cd-details" to={`/client/projects/details/${project._id}/${encoded}`}>
                              Details
                            </NavLink>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="mvp-cd-side">
              <section className={`mvp-cd-card ${detailsOpen ? "" : "is-shut"}`}>
                <h2>
                  Client Details
                  <button type="button" className="mvp-cd-fold" aria-expanded={detailsOpen} onClick={() => setDetailsOpen((open) => !open)}>
                    <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
                      <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="#6A7988" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                  </button>
                </h2>
                <dl>
                  <div>
                    <dt>Customer since</dt>
                    <dd>{clientLoading ? <span className="mvp-cd-skel mvp-cd-skel-line" /> : (client?.created_at ? moment(client.created_at).format("MMMM YYYY") : "—")}</dd>
                  </div>
                  <div>
                    <dt>Point of Contact</dt>
                    <dd>{clientLoading ? <span className="mvp-cd-skel mvp-cd-skel-line" /> : (client?.poc_user_name || "—")}</dd>
                  </div>
                  <div>
                    <dt>Contact Email</dt>
                    <dd>{clientLoading ? <span className="mvp-cd-skel mvp-cd-skel-line is-wide" /> : (client?.poc_email || client?.email || "—")}</dd>
                  </div>
                  <div>
                    <dt>Contact Phone</dt>
                    <dd>{clientLoading ? <span className="mvp-cd-skel mvp-cd-skel-line" /> : (client?.mobile_no || client?.billing_mobile_no || "—")}</dd>
                  </div>
                </dl>
              </section>
              <section className={`mvp-cd-card ${managerOpen ? "" : "is-shut"}`}>
                <h2>
                  Account Manager
                  <button type="button" className="mvp-cd-fold" aria-expanded={managerOpen} onClick={() => setManagerOpen((open) => !open)}>
                    <svg width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
                      <path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="#6A7988" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                  </button>
                </h2>
                {clientLoading ? (
                  <div className="mvp-cd-manager mvp-cd-skel-manager">
                    <span className="mvp-cd-skel mvp-cd-skel-avatar" />
                    <span className="mvp-cd-skel mvp-cd-skel-line" />
                  </div>
                ) : client?.acc_manager ? (
                  <div className="mvp-cd-manager">
                    <ManagerAvatar manager={client.acc_manager} />
                    <span>{personName(client.acc_manager) || "Account Manager"}</span>
                  </div>
                ) : (
                  <p className="mvp-cd-muted">Account manager is not assigned</p>
                )}
              </section>
            </aside>
          </div>
        </div>
      </div>
    </MvpClientChrome>
  );
}
